// src/app/calendar.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity } from 'react-native';
import axios from 'axios';
import {
  format, parseISO, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, isSameMonth, isSameDay
} from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';
import API_BASE_URL from '../apiConfig.js';
import { IoCalendarOutline, IoCalendar } from 'react-icons/io5';
import { MdEmail } from 'react-icons/md';

// token helper (web)
const getToken = () => {
  try { return localStorage.getItem('userToken'); } catch { return null; }
};

// Mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      // Check for mobile device using multiple methods
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      // Consider it mobile if it matches mobile user agent OR has small screen with touch support
      setIsMobile(isMobileUA || (isSmallScreen && hasTouchSupport));
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

/* ---------- Tiny no-deps calendar ---------- */
function CalendarMini({ monthDate, selectedDate, markedDates = {}, onDayPress, onMonthChange }) {
  const start = startOfWeek(startOfMonth(monthDate), { weekStartsOn: 0 });
  const end   = endOfWeek(endOfMonth(monthDate), { weekStartsOn: 0 });

  const days = [];
  for (let d = start; d <= end; d = addDays(d, 1)) days.push(d);

  const monthLabel = format(monthDate, 'MMMM yyyy');
  const weekHeaders = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  const goPrev = () => onMonthChange(addDays(startOfMonth(monthDate), -1));
  const goNext = () => onMonthChange(addDays(endOfMonth(monthDate), 1));
  const toStr  = (d) => format(d, 'yyyy-MM-dd');

  return (
    <View style={cal.container}>
      <View style={cal.header}>
        <TouchableOpacity onPress={goPrev}><Text style={cal.nav} aria-hidden>‹</Text></TouchableOpacity>
        <Text style={cal.title}>{monthLabel}</Text>
        <TouchableOpacity onPress={goNext}><Text style={cal.nav} aria-hidden>›</Text></TouchableOpacity>
      </View>

      <View style={cal.grid}>
        {weekHeaders.map((h) => (
          <Text key={h} style={[cal.cell, cal.dow]}>{h}</Text>
        ))}

        {days.map((d, i) => {
          const inMonth = isSameMonth(d, monthDate);
          const dStr = toStr(d);
          const isSel = selectedDate && isSameDay(d, parseISO(selectedDate));
          const isToday = isSameDay(d, new Date());
          const marked = !!markedDates[dStr];

          return (
            <TouchableOpacity
              key={i}
              style={[
                cal.cell,
                cal.day,
                !inMonth && cal.dim,
                isSel && cal.selected,
                isToday && cal.todayBorder,
              ]}
              onPress={() => onDayPress({ dateString: dStr })}
            >
              <Text style={[cal.dayText, !inMonth && cal.dimText]}>{format(d, 'd')}</Text>
              {marked ? <View style={cal.dot} /> : null}
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
}

/* ---------- Page ---------- */
export default function CalendarPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const [monthDate, setMonthDate] = useState(new Date());
  const [markedDates, setMarkedDates] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);

  const [monthlyJobs, setMonthlyJobs] = useState([]); // all jobs for visible month
  const [filterSelected, setFilterSelected] = useState(true); // Start with filtering by selected date
  const [userPermissions, setUserPermissions] = useState(null);
  const [customersForEmails, setCustomersForEmails] = useState([]); // Add state for customer emails

  const [completionOpen, setCompletionOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);

  // Load user permissions on component mount and check access
  useEffect(() => {
    const loadUserPermissions = () => {
      try {
        const permissions = localStorage.getItem('userPermissions');
        if (permissions) {
          const parsedPermissions = JSON.parse(permissions);
          setUserPermissions(parsedPermissions);
          
          const user_type = parsedPermissions?.user_type;
          const jobPerm = parsedPermissions?.job_perm;

          // Block access if user is NOT (Owner OR Admin) AND has no invoice permission
          if ((user_type !== 'owner' && user_type !== 'admin') && jobPerm === 'none') {
            alert("You don't have permission to view jobs. Please contact your account administrator.");
            navigate('/'); // Redirect to home page
            return;
          }
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      }
    };
    loadUserPermissions();
  }, [navigate]);

  // Reset all state when returning to the calendar page
  useEffect(() => {
    // Clear all cached data
    setMonthDate(new Date());
    setMarkedDates({});
    setSelectedDate(null);
    setMonthlyJobs([]);
    setCustomersForEmails([]);
    setFilterSelected(true);
    setCompletionOpen(false);
    setInvoiceOpen(false);
    setQuoteOpen(false);
    setCurrentJob(null);
    
    // Force reload of data
    const loadMonth = async () => {
      try {
        const token = getToken();
        const monthStr = format(new Date(), 'yyyy-MM');
        const { data: jobs } = await axios.get(`${API_BASE_URL}/api/jobs?month=${monthStr}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        const marks = {};
        const normalized = [];
        jobs.forEach((job) => {
          const utc = new Date(`${job.selectedDate}T${job.selectedTime}Z`);
          const local = new Date(utc.getTime() - utc.getTimezoneOffset() * 60000);
          const key = local.toISOString().split('T')[0];
          marks[key] = { marked: true };
          normalized.push(job);
        });
        setMarkedDates(marks);
        setMonthlyJobs(normalized);
      } catch (err) {
        console.error('Error fetching month jobs:', err);
        setMonthlyJobs([]);
        setMarkedDates({});
      }
    };
    
    loadMonth();
    loadCustomersForEmails();
  }, [location.pathname]); // Trigger when pathname changes (navigating back to calendar)

  // Check if user has job write permissions
  const hasJobWritePermission = () => {
    return userPermissions?.job_perm === 'write';
  };

  // Check if user can see prices
  const canSeePrices = () => {
    return userPermissions?.price_perm === 'visible';
  };

  // Check if user can complete/undo a job
  const canCompleteUndoJob = (job) => {
    if (!userPermissions) return false;
    
    // Owner and admin can always complete/undo
    if (userPermissions.user_type === 'owner' || userPermissions.user_type === 'admin') {
      return true;
    }
    
    // If user has read-only job permissions, they cannot complete/undo
    if (userPermissions.job_perm === 'read') {
      return false;
    }
    
    // Check if user is assigned to this job
    const isAssignedToJob = job.assigned_type === 'all' || 
      (job.assigned_type === 'specific' && job.assignedUserIds?.includes(userPermissions.userId));
    
    // If assigned to job and has write permission, allow
    if (isAssignedToJob && userPermissions.job_perm === 'write') {
      return true;
    }
    
    // If NOT assigned but has edit_all_jobs permission and write access, allow
    if (!isAssignedToJob && userPermissions.edit_all_jobs === 1 && userPermissions.job_perm === 'write') {
      return true;
    }
    
    // Otherwise, disable
    return false;
  };

  // Load customers to get email addresses
  const loadCustomersForEmails = async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API_BASE_URL}/api/customers`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const customerList = Array.isArray(res.data) ? res.data : [];
      setCustomersForEmails(customerList);
    } catch (err) {
      console.error('Error loading customers for emails:', err);
    }
  };

  // Helper function to get customer email
  const getCustomerEmail = (customerName) => {
    const customer = customersForEmails.find(c => c?.name === customerName);
    return customer?.email || null;
  };

  // Email customer function
  const emailCustomer = (email) => {
    if (email) {
      window.open(`mailto:${email}`, '_self');
    }
  };

  // load marks + month jobs
  useEffect(() => {
    const loadMonth = async () => {
      try {
        const token = getToken();
        const monthStr = format(monthDate, 'yyyy-MM');
        const { data: jobs } = await axios.get(`${API_BASE_URL}/api/jobs?month=${monthStr}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        // mark days with any jobs
        const marks = {};
        const normalized = [];
        jobs.forEach((job) => {
          const utc = new Date(`${job.selectedDate}T${job.selectedTime}Z`);
          const local = new Date(utc.getTime() - utc.getTimezoneOffset() * 60000);
          const key = local.toISOString().split('T')[0];
          marks[key] = { marked: true };
          normalized.push(job);
        });
        setMarkedDates(marks);
        setMonthlyJobs(normalized);
      } catch (err) {
        console.error('Error fetching month jobs:', err);
        setMonthlyJobs([]);
        setMarkedDates({});
      }
    };
    loadMonth();
    loadCustomersForEmails(); // Load customers for email lookups
  }, [monthDate]);

  const handleDayPress = ({ dateString }) => {
    setSelectedDate(dateString);
    setFilterSelected(true); // auto-filter to the clicked day
  };

  const monthLabel = format(monthDate, 'MMMM yyyy');

  const jobsList = useMemo(() => {
    const sortedJobs = [...monthlyJobs].sort((a, b) => {
      const dateA = new Date(`${a.selectedDate}T${a.selectedTime}Z`);
      const dateB = new Date(`${b.selectedDate}T${b.selectedTime}Z`);
      return dateA - dateB;
    });

    if (!filterSelected || !selectedDate) {
      const startOfMonthDate = startOfMonth(monthDate);
      const endOfMonthDate = endOfMonth(monthDate);
      return sortedJobs.filter((job) => {
        const jobDate = parseISO(job.selectedDate);
        return jobDate >= startOfMonthDate && jobDate <= endOfMonthDate;
      });
    }

    return sortedJobs.filter((job) => {
      const utc = new Date(`${job.selectedDate}T${job.selectedTime}Z`);
      const local = new Date(utc.getTime() - utc.getTimezoneOffset() * 60000);
      const localKey = local.toISOString().split('T')[0];
      return localKey === selectedDate;
    });
  }, [monthlyJobs, filterSelected, selectedDate, monthDate]);

  const formatTime = (date, time) => {
    if (!date || !time) return '';
    const d = new Date(`${date}T${time}Z`);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const handleFilterToggle = () => {
    setFilterSelected((v) => !v);
  };

  const completeJob = async (job) => {
    setCurrentJob(job);
    setCompletionOpen(true);
  };

  const handleCompletionConfirm = async () => {
    const job = currentJob;
    setCompletionOpen(false);

    try {
      const token = getToken();
      await axios.put(
        `${API_BASE_URL}/api/jobs/edit/${job.id}`,
        { completed: 'yes' },
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );

      // Update the job in the local state immediately
      setMonthlyJobs(prevJobs => 
        prevJobs.map(existingJob => 
          existingJob.id === job.id 
            ? { ...existingJob, completed: 'yes' }
            : existingJob
        )
      );

      // Check permissions before showing quote/invoice creation modals
      if (job.visitType === 'quote') {
        // Only show quote modal if user has quote write permission
        if (userPermissions?.quote_perm === 'write') {
          setQuoteOpen(true);
        }
      } else {
        // Only show invoice modal if user has invoice write permission
        if (userPermissions?.invoice_perm === 'write') {
          setInvoiceOpen(true);
        }
      }
    } catch (e) {
      console.error('Error completing job', e);
      window.alert('Failed to update job completion. Please try again later.');
    }
  };

  const handleInvoiceConfirm = () => {
    const job = currentJob;
    setInvoiceOpen(false);
    const q = new URLSearchParams({
      customerName: job.customerName ?? '',
      title: job.title ?? '',
      description: job.description ?? '',
      price: String(job.price ?? ''),
      selectedDate: job.selectedDate ?? '',
      selectedTime: job.selectedTime ?? '',
      jobId: String(job.id),
    }).toString();
    navigate(`/create-invoice?${q}`);
  };

  const handleInvoiceCancel = () => {
    setInvoiceOpen(false);
  };

  const handleQuoteConfirm = () => {
    const job = currentJob;
    setQuoteOpen(false);
    const q = new URLSearchParams({
      id: String(job.id),
      customerId: String(job.customerId ?? ''),
      customerName: job.customerName ?? '',
      title: job.title ?? '',
      description: job.description ?? '',
      price: String(job.price ?? ''),
    }).toString();
    navigate(`/create-quote?${q}`);
  };

  const handleQuoteCancel = () => {
    setQuoteOpen(false);
  };

  const undoComplete = async (job) => {
    try {
      const token = getToken();
      await axios.put(
        `${API_BASE_URL}/api/jobs/edit/${job.id}`,
        { completed: 'no' },
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      
      // Update the job in the local state immediately
      setMonthlyJobs(prevJobs => 
        prevJobs.map(existingJob => 
          existingJob.id === job.id 
            ? { ...existingJob, completed: 'no' }
            : existingJob
        )
      );
    } catch (e) {
      console.error('Error undoing job completion', e);
      window.alert('Failed to undo job completion. Please try again later.');
    }
  };

  return (
    <View style={styles.page}>
      <View style={[styles.content, isMobile && styles.contentMobile]}>
        {/* LEFT: bigger calendar */}
        <View style={[styles.leftPane, isMobile && styles.leftPaneMobile]}>
          <TouchableOpacity
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              alignSelf: 'center',
              marginTop: 10,
              marginBottom: 10,
              backgroundColor: '#007AFF',
              paddingVertical: 6,
              paddingHorizontal: 12,
              borderRadius: 6,
            }}
            onPress={() => navigate('/schedule')}
          >
            <IoCalendar size={16} color="#fff" style={{ marginRight: 6 }} />
            <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold' }}>Weekly View</Text>
          </TouchableOpacity>
          <CalendarMini
            monthDate={monthDate}
            selectedDate={selectedDate}
            markedDates={markedDates}
            onDayPress={handleDayPress}
            onMonthChange={(d) => setMonthDate(startOfMonth(d))}
          />

          {/* simple filter chip / month label */}
          <View style={styles.leftInfo}>
            <Text style={styles.monthText}>{monthLabel}</Text>
            <TouchableOpacity
              style={[styles.chip, filterSelected && styles.chipActive]}
              onPress={handleFilterToggle}
            >
              <Text style={styles.chipText}>
                {filterSelected && selectedDate ? `Filtering: ${format(parseISO(selectedDate), 'MMM d')}` : `Showing All ${monthLabel} Jobs`}
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* RIGHT: all jobs list (scrollable) */}
        <View style={[styles.rightPane, isMobile && styles.rightPaneMobile]}>
          <View style={styles.listHeader}>
            <Text style={styles.listHeaderText}>
              {filterSelected && selectedDate
                ? `Jobs on ${format(parseISO(selectedDate), 'MMM d, yyyy')} — ${jobsList.length} total jobs`
                : `All jobs in ${monthLabel} — ${jobsList.length} total jobs`}
            </Text>
          </View>

          {jobsList.length > 0 ? (
            <FlatList
              data={jobsList}
              keyExtractor={(item, idx) => String(item.id ?? idx)}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.card}
                  onPress={() => navigate(`/job-details?id=${encodeURIComponent(item.id)}`)}
                >
                  <Text
                    style={[
                      styles.topLeftLabel,
                      {
                        backgroundColor: item.visitType === 'job' ? 'green' : 'lightblue',
                        color: item.visitType === 'job' ? 'white' : 'black',
                      },
                    ]}
                  >
                    {item.visitType === 'job' ? 'Job' : 'Quote'}
                  </Text>
                  <View style={[styles.dateContainer, { flexDirection: 'row', alignItems: 'center' }]}>  
                      <IoCalendarOutline size={16} color="green" style={{ marginRight: 6 }} aria-hidden />
                      <Text style={styles.timeText}>{new Date(`${item.selectedDate}T${item.selectedTime}Z`).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })} {formatTime(item.selectedDate, item.selectedTime)}</Text>
                    </View>
                  <View style={[styles.cardHeader, { marginTop: 24 }]}>  
                    <Text style={styles.jobTitle}>{item.title}</Text>
                  </View>
                  <Text style={styles.jobDetail}>{`Description: ${item.description || 'No Description'}`}</Text>
                  <Text style={styles.jobDetail}>{`Customer: ${item.customerName}`}</Text>
                  <Text style={styles.jobDetail}>{`${item.address || 'No Address'}`}</Text>
                  <Text style={styles.jobDetail}>{`${item.city || 'No City'}, ${item.state || 'No State'} ${item.zip || 'No Zip'}`}</Text>
                  {canSeePrices() && <Text style={styles.jobDetail}>{`Price: $${item.price}`}</Text>}
                  <Text style={styles.jobDetail}>{`Assigned To: ${item.assignedUserNames || 'Not assigned'}`}</Text>
                  
                  {/* Bottom row - layout changes based on whether email exists */}
                  {getCustomerEmail(item.customerName) ? (
                    // When email exists: Email on left, Complete/Undo on right (same row)
                    <View style={styles.bottomRowWithEmail}>
                      <TouchableOpacity
                        onPress={(e) => {
                          e.stopPropagation();
                          emailCustomer(getCustomerEmail(item.customerName));
                        }}
                        style={styles.iconButtonGreen}
                      >
                        <MdEmail size={20} color="white" />
                      </TouchableOpacity>
                      
                      {item.completed === 'no' ? (
                        <TouchableOpacity
                          style={[styles.completeButton, !canCompleteUndoJob(item) && { backgroundColor: '#ccc' }]}
                          onPress={(e) => {
                            e.stopPropagation();
                            canCompleteUndoJob(item) && completeJob(item);
                          }}
                          disabled={!canCompleteUndoJob(item)}
                        >
                          <Text style={styles.completeButtonText}>
                            {canCompleteUndoJob(item) ? '✔ Complete' : 'Incomplete'}
                          </Text>
                        </TouchableOpacity>
                      ) : (
                        <View style={styles.completedContainer}>
                          <Text style={styles.completedText}>✔ Completed</Text>
                          {canCompleteUndoJob(item) && (
                            <TouchableOpacity
                              onPress={(e) => {
                                e.stopPropagation();
                                undoComplete(item);
                              }}
                            >
                              <Text style={styles.undoText}>Undo</Text>
                            </TouchableOpacity>
                          )}
                        </View>
                      )}
                    </View>
                  ) : (
                    // When NO email: Complete/Undo on separate row (right-aligned)
                    <View style={styles.completeButtonRow}>
                      {item.completed === 'no' ? (
                        <TouchableOpacity
                          style={[styles.completeButton, !canCompleteUndoJob(item) && { backgroundColor: '#ccc' }]}
                          onPress={(e) => {
                            e.stopPropagation();
                            canCompleteUndoJob(item) && completeJob(item);
                          }}
                          disabled={!canCompleteUndoJob(item)}
                        >
                          <Text style={styles.completeButtonText}>
                            {canCompleteUndoJob(item) ? '✔ Complete' : 'Incomplete'}
                          </Text>
                        </TouchableOpacity>
                      ) : (
                        <View style={styles.completedContainer}>
                          <Text style={styles.completedText}>✔ Completed</Text>
                          {canCompleteUndoJob(item) && (
                            <TouchableOpacity
                              onPress={(e) => {
                                e.stopPropagation();
                                undoComplete(item);
                              }}
                            >
                              <Text style={styles.undoText}>Undo</Text>
                            </TouchableOpacity>
                          )}
                        </View>
                      )}
                    </View>
                  )}
                </TouchableOpacity>
              )}
              style={{ height: '100%' }}
            />
          ) : (
            <Text style={styles.noJobsText}>No jobs.</Text>
          )}
        </View>
      </View>

      {/* Completion Modal */}
      {completionOpen && (
        <View style={styles.overlay}>
          <View style={styles.overlayCard}>
            <Text style={styles.modalTitle}>Complete Job</Text>
            <Text style={styles.modalMessage}>Has this visit been completed?</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={styles.modalButton} onPress={handleCompletionConfirm}>
                <Text style={styles.modalButtonText}>Yes</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalButton} onPress={() => setCompletionOpen(false)}>
                <Text style={styles.modalButtonText}>No</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Invoice Modal */}
      {invoiceOpen && (
        <View style={styles.overlay}>
          <View style={styles.overlayCard}>
            <Text style={styles.modalTitle}>Create Invoice</Text>
            <Text style={styles.modalMessage}>Would you like to create an invoice now?</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={styles.modalButton} onPress={handleInvoiceConfirm}>
                <Text style={styles.modalButtonText}>Yes</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalButton} onPress={handleInvoiceCancel}>
                <Text style={styles.modalButtonText}>No</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Quote Modal */}
      {quoteOpen && (
        <View style={styles.overlay}>
          <View style={styles.overlayCard}>
            <Text style={styles.modalTitle}>Create Quote</Text>
            <Text style={styles.modalMessage}>Would you like to create a quote to send to the customer?</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity style={styles.modalButton} onPress={handleQuoteConfirm}>
                <Text style={styles.modalButtonText}>Yes</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalButton} onPress={handleQuoteCancel}>
                <Text style={styles.modalButtonText}>No</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}
    </View>
  );
}

/* ---------- Styles ---------- */
const styles = StyleSheet.create({
  page: {
    minHeight: '100vh',
    backgroundColor: '#fff',
    padding: 12,
  },
  content: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
  },
  contentMobile: {
    flexDirection: 'column',
  },
  leftPane: {
    width: 460,            // bigger calendar column
  },
  leftPaneMobile: {
    width: '100%',
  },
  leftInfo: {
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  monthText: { fontWeight: '700', color: '#111827' },
  chip: { borderWidth: 1, borderColor: '#cbd5e1', borderRadius: 999, paddingVertical: 4, paddingHorizontal: 10 },
  chipActive: { backgroundColor: '#e6f0ff', borderColor: '#c7dcff' },
  chipText: { fontSize: 12, color: '#1f2937' },

  rightPane: {
    flex: 1,
    minHeight: '70vh',
    maxHeight: 'calc(100vh - 24px)', // right side scrolls independently
    overflow: 'auto',
    borderLeftWidth: 1,
    borderLeftColor: '#f0f0f0',
    paddingLeft: 12,
  },
  rightPaneMobile: {
    width: '100%',
    borderLeftWidth: 0,
    paddingLeft: 0,
  },
  listHeader: { marginBottom: 8 },
  listHeaderText: { fontSize: 16, fontWeight: '700', color: '#333' },

  card: { backgroundColor: '#f9f9f9', borderRadius: 8, padding: 12, marginBottom: 10, borderWidth: 1, borderColor: '#eee' },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  dateContainer: { position: 'absolute', top: 8, right: 8, alignItems: 'center', gap: 4 },
  jobTitle: { fontWeight: '700', fontSize: 15, textDecorationLine: 'underline' },
  timeText: { fontSize: 13, color: '#2f855a' },
  jobDetail: { fontSize: 13, color: '#555', marginTop: 2 },

  topLeftLabel: {
    position: 'absolute', top: 8, left: 8,
    backgroundColor: 'lightblue', paddingHorizontal: 5, paddingVertical: 2,
    borderRadius: 4, fontWeight: 'bold', color: 'black', zIndex: 1,
  },

  completeButton: { marginTop: 6, alignSelf: 'flex-end', backgroundColor: '#28a745', paddingVertical: 6, paddingHorizontal: 10, borderRadius: 4 },
  completeButtonText: { color: '#fff', fontWeight: 'bold', fontSize: 13 },
  completedContainer: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', marginTop: 6 },
  completedText: { color: 'green', fontWeight: 'bold', marginRight: 8, fontSize: 13 },
  undoText: { color: 'blue', textDecorationLine: 'underline', fontSize: 13 },
  
  completeButtonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: 10,
  },

  bottomRowWithEmail: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 10,
  },

  iconButtonGreen: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'green',
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 5,
    cursor: 'pointer',
  },

  noJobsText: { textAlign: 'center', color: '#777', fontSize: 14, paddingTop: 12 },

  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  overlayCard: {
    width: 300,
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  modalMessage: {
    fontSize: 14,
    marginBottom: 16,
    textAlign: 'center',
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  modalButton: {
    flex: 1,
    backgroundColor: '#007AFF',
    paddingVertical: 8,
    borderRadius: 4,
    marginHorizontal: 4,
  },
  modalButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    textAlign: 'center',
  },
});

/* ---------- Calendar styles ---------- */
const cal = StyleSheet.create({
  container: {
    width: '100%', maxWidth: 460, alignSelf: 'flex-start',
    borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 8, padding: 8, backgroundColor: '#fff'
  },
  calendarBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    marginTop: 10,
    marginBottom: 10,
    backgroundColor: '#007AFF',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  calendarBtnText: { color: '#fff', fontSize: 14, fontWeight: 'bold' },

  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  title: { fontSize: 18, fontWeight: '700', color: '#111827' },
  nav: { fontSize: 20, paddingHorizontal: 8, color: '#111827' },
  grid: { flexDirection: 'row', flexWrap: 'wrap' },
  cell: { width: `${100/7}%`, alignItems: 'center', justifyContent: 'center', height: 42 },
  dow: { fontSize: 12, fontWeight: '600', color: '#6b7280' },
  day: {
    height: 44, marginVertical: 2, borderRadius: 8,
    borderWidth: 1, borderColor: '#fff'
  },
  dayText: { fontSize: 14, color: '#111827' },
  dim: { backgroundColor: '#fafafa' },
  dimText: { color: '#9ca3af' },
  selected: { backgroundColor: '#e6f0ff', borderColor: '#c7dcff' },
  todayBorder: { borderColor: '#60a5fa' },
  dot: { width: 6, height: 6, borderRadius: 6, backgroundColor: '#007AFF', marginTop: 2 },
});
